<template>
  <div class="trip-form">
    <toolbar
      :backRoute="$route.params.aircraft_id ? '/dashboard/' : '/dashboard/trips'"
      :beforeBack="checkSchedule"
      :title="toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="trip-item-delete"
      saveButtonLabel="Save trip"
      saveButtonEvent="trip-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="trip-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="4">
              <div class="form-group">
                <label for="name">Reference:</label>
                  <d-input
                    v-model="data.reference"
                    :readonly="true" />
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label>Customer:</label>
                <br>
                <span class="text-primary" style="line-height: 32px;">
                  {{ aircraftCustomer.name }}
                </span>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label for="aircraft_id">Aircraft:</label>

                <validation-provider
                  v-if="!isEdit"
                  rules="required"
                  name="aircraft_id"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="data.aircraft_id"
                    :state="errors.length ? 'invalid' : null"
                    :options="aircraftsOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>

                <template v-else>
                  <br><span style="line-height: 32px;">{{ aircraftName }}</span>
                </template>
              </div>
            </d-col>

            <d-col cols="2">
              <div class="form-group text-right">
                <label>Show on pilot panel:</label>
                <br>
                <d-checkbox
                  inline
                  v-model="data.show_on_aircraft_api"
                  toggle
                  toggle-small
                  class="mr-0" />
              </div>
            </d-col>
          </d-row>
        </validation-observer>

        <flights-plan-creator
          v-if="allDataLoaded"
          ref="flights-plan"
          :aircraft-id="data.aircraft_id"
          :aircrafts="aircrafts"
          :airports="$root.airports"
          :customers="customers"
          :handlers="handlers"
          :trip-id="data.id"
          :aircraft-customer-id="aircraftCustomer.id" />

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>
    </d-container>
  </div>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FlightsPlanCreator from './../form-parts/FlightsPlanCreator.vue';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import Vue from 'vue';

export default {
  name: 'trip-form',
  components: {
    'flights-plan-creator': FlightsPlanCreator
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedAircrafts && this.$root.loadedAirports && this.loadedCustomers && this.loadedHandlers;
    },
    aircraftsOptions () {
      return CommonGetters.getAircraftsForSelect(this.aircrafts);
    },
    aircraftCustomer () {
      if (!this.data.aircraft_id) {
        return '(none)';
      }

      let selectedAircraft = this.aircrafts.filter(aircraft => aircraft.id === this.data.aircraft_id);

      if (selectedAircraft.length === 0) {
        return '(none)';
      }

      let customerID = selectedAircraft[0].customer_id;
      let foundedCustomer = this.customers.filter(customer => customer.id === customerID);

      if (foundedCustomer.length === 0) {
        return '(none)';
      }

      return {
        id: foundedCustomer[0].id,
        name: foundedCustomer[0].name
      };
    },
    customersOptions () {
      return CommonGetters.getCustomersForSelect(this.customers);
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new trip';
      }

      return 'Edit trip';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new') {
        return false;
      }

      return 'Delete trip';
    },
    aircraftName () {
      let index = this.aircrafts.findIndex(aircraft => aircraft.id === this.data.aircraft_id);

      if (index === -1) {
        return '??? (ID: ' + this.data.aircraft_id + ')';
      }

      return this.aircrafts[index].regin_mark;
    }
  },
  data () {
    return {
      data: {
        id: 0,
        reference: '',
        aircraft_id: null,
        show_on_aircraft_api: false
      },
      aircrafts: [],
      customers: [],
      handlers: [],
      loadError: false,
      loadedData: false,
      loadedAircrafts: false,
      loadedCustomers: false,
      loadedHandlers: false,
      removeInProgress: false,
      saveInProgress: false,
      originalData: {}
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
      this.loadAdditionalData();
      this.loadVariableAdditionalData();
    } else {
      this.loadData();
    }

    if (!this.isEdit) {
      this.originalData = JSON.stringify(this.data);
    } else {
      this.$watch('allDataLoaded', (newValue, oldValue) => {
        if (newValue && !oldValue) {
          Vue.nextTick(() => {
            this.originalData = JSON.stringify(this.data);
          }, 250);
        }
      });
    }

    if (this.$route.params.aircraft_id) {
      this.data.aircraft_id = parseInt(this.$route.params.aircraft_id, 10);
    }

    this.$bus.$on('trip-item-delete', this.removeTrip);
    this.$bus.$on('trip-item-save', this.validateForm);
    this.$bus.$on('handlers-list-reload', this.loadVariableAdditionalData);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/trips/item/',
        instanceFields: ['id', 'reference', 'aircraft_id', 'show_on_aircraft_api'],
        booleanFields: ['show_on_aircraft_api'],
        loadedKey: 'loadedData'
      });

      this.loadAdditionalData();
      this.loadVariableAdditionalData();
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/aircrafts/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedAircrafts',
        outputKey: 'aircrafts',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/items?fields=id,name,airport_handlers',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedCustomers',
        outputKey: 'customers',
        errorKey: 'loadError'
      });
    },
    loadVariableAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/handlers/items?simple=1',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedHandlers',
        outputKey: 'handlers',
        errorKey: 'loadError'
      });
    },
    removeTrip () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/trips/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Trip removed',
        successText: 'The selected trip has been removed',
        confirmTitle: 'Do you really want to remove this trip?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/trips');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      if (!this.$refs['flights-plan'].hasUnsavedChanges) {
        FormUtils.validate(this.$refs['trip-form'], this.submitForm);
        return;
      }

      Vue.swal({
        title: 'You have unsaved flight schedule',
        html: 'Do you want to continue - you will lose all changes in the flight schedule.',
        icon: 'question',
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        showCancelButton: true,
        buttonsStyling: true
      }).then((result) => {
        if (result.value) {
          FormUtils.validate(this.$refs['trip-form'], this.submitForm);
        }
      }).catch((errors) => {
        console.log(errors);
      });
    },
    submitForm () {
      let endpointUrl = '/trips/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/trips/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'Trip added' : 'Trip saved',
        successText: 'The trip has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: () => {
          this.$router.push('/dashboard/trips');
        }
      });
    },
    checkSchedule (successCallback) {
      if (!this.$refs['flights-plan'].hasUnsavedChanges) {
        if (JSON.stringify(this.data) === this.originalData) {
          successCallback();
          return;
        }

        Vue.swal({
          title: 'You have unsaved changes',
          html: 'Do you want to continue - you will lose all changes.',
          icon: 'question',
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          showCancelButton: true,
          buttonsStyling: true
        }).then((result) => {
          if (result.value) {
            successCallback();
          }
        }).catch((errors) => {
          console.log(errors);
        });
      } else {
        Vue.swal({
          title: 'You have unsaved flight schedule',
          html: 'Do you want to continue - you will lose all changes in the flight schedule.',
          icon: 'question',
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          showCancelButton: true,
          buttonsStyling: true
        }).then((result) => {
          if (result.value) {
            successCallback();
          }
        }).catch((errors) => {
          console.log(errors);
        });
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('trip-item-delete', this.removeTrip);
    this.$bus.$off('trip-item-save', this.validateForm);
  }
}
</script>
